import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Loader2, Cable, ShieldOff } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SeedBadge } from "@/components/common/SeedBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { INTEGRATION_STATUS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

export default function Integrations() {
  const { can } = useAuth();
  const writable = can("integrations", "write");
  const [items, setItems] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/integrations").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/bridge/status").then((r) => setBridge(r.data)).catch(() => setBridge(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (it) => {
    setBusy(it.id);
    const next = it.status === "connected" ? "disconnected" : "connected";
    try {
      await api.patch(`/integrations/${it.id}`, { status: next });
      toast.success(next === "connected" ? "Verbunden" : "Getrennt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const bConnected = bridge && bridge.connected;

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integrationen" subtitle="Verbundene FUNDIO-Systeme und externe Dienste." />

      {/* FUNDIO Bridge status */}
      <div data-testid="bridge-status-card" className="card-surface mb-6 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-fundio-gold/10"><Cable className="h-5 w-5 text-fundio-gold-deep" /></span>
            <div>
              <p className="text-sm font-semibold text-fundio-ink">FUNDIO Bridge</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-fundio-faint">{bridge ? bridge.api_prefix : "…"} · read-only</p>
            </div>
          </div>
          <StatusBadge map={INTEGRATION_STATUS} value={bConnected ? "connected" : "disconnected"} testid="bridge-status-badge" />
        </div>
        {bridge && (
          <>
            {!bConnected && (
              <p className="mt-3 rounded-lg border border-fundio-border bg-fundio-subtle px-3 py-2 text-xs text-fundio-muted">{bridge.reason}</p>
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {bridge.resources?.map((r) => (
                <div key={r.key} className="rounded-lg border border-fundio-border bg-white px-3 py-2" data-testid={`bridge-resource-${r.key}`}>
                  <div className="text-xs font-medium text-fundio-ink">{r.label}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] text-fundio-faint">read-only</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg/50 px-3 py-2 text-xs text-status-warning">
              <ShieldOff className="h-3.5 w-3.5" /> Schreibfunktionen sind <span className="font-semibold">vorbereitet, aber deaktiviert</span> (dry-run). WordPress/Directorist bleibt Master.
            </div>
          </>
        )}
      </div>

      <div className="mb-6 rounded-xl border border-fundio-border bg-white px-4 py-3">
        <p className="text-xs text-fundio-muted">Bestehende APIs (app.fundio.ch, fundio.ch) werden über die <span className="font-medium text-fundio-ink">FUNDIO Bridge</span> in einer späteren Phase <span className="font-medium text-fundio-ink">kontrolliert</span> angebunden. In Phase 2A wird der Verbindungsstatus verwaltet und READ-ONLY vorbereitet.</p>
      </div>

      {items === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={Plug} title="Keine Integrationen" testid="integrations-empty" />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((it) => (
            <div key={it.id} data-testid={`integration-card-${it.id}`} className="card-surface flex items-center justify-between rounded-xl p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-fundio-gold/10"><Plug className="h-5 w-5 text-fundio-gold-deep" /></span>
                <div><p className="text-sm font-medium text-fundio-ink">{it.name}<SeedBadge show={it.is_seed} /></p><p className="font-mono text-[10px] uppercase tracking-wider text-fundio-faint">{it.type} · Sync {it.last_sync ? formatDate(it.last_sync) : "nie"}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge map={INTEGRATION_STATUS} value={it.status} />
                {writable && <Switch data-testid={`integration-toggle-${it.id}`} checked={it.status === "connected"} disabled={busy === it.id} onCheckedChange={() => toggle(it)} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
